import React, { useMemo, useRef, useState } from 'react';

import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { Box, Stack, Typography } from '@mui/material';

import dayjs from '@/components/NewUI/utils/dayjs';
import { withScoutTheme } from '@/components/ThemeContext/ThemeContext';

import { bookCall } from './api';
import BookingShell, { PageTitle } from './BookingShell';
import BookingDeadEnd from './BookingStates';
import BookingSummary from './BookingSummary';
import { CallSummaryPanel, MobileConfirmBar } from './CallSummary';
import PhoneChoice from './PhoneChoice';
import { bookingLimits, buildSlots, displayPhone, phoneError } from './scheduling';
import SlotPicker from './SlotPicker';
import { styles } from './styles';
import TimeZoneControl from './TimeZoneControl';

import type { ICallSummary } from './CallSummary';
import type {
  BookingPageState,
  IBooking,
  IBookingPageProps,
  PhoneChoice as PhoneChoiceValue
} from './types';

export interface IProps extends IBookingPageProps {
  // Production reads the device zone; tests pass a fixed one.
  detectTimeZone?: () => string;
}

const deviceTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const TRY_AGAIN = 'Something went wrong. Please try again.';

// The public page a candidate opens from the booking email (ADR 0009),
// mounted by AiVoiceInterviewBookingsController as an independent root, so it
// wraps itself in the Scout theme. The server decides the state and re-checks
// every booking; this page only helps the candidate pick a valid one.
const BookingPage = ({
  state,
  companyName,
  link,
  nowIso,
  bookUrl,
  booking,
  detectTimeZone = deviceTimeZone
}: IProps) => {
  const detectedTimeZone = useMemo(() => detectTimeZone(), [detectTimeZone]);
  const [timeZone, setTimeZone] = useState(detectedTimeZone);
  const [pageState, setPageState] = useState<BookingPageState>(state);
  const [confirmedBooking, setConfirmedBooking] = useState<IBooking | undefined>(booking);
  const [showLaterDates, setShowLaterDates] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [rejected, setRejected] = useState<Set<string>>(() => new Set());
  const [errorSlot, setErrorSlot] = useState<string | null>(null);
  const [phoneChoice, setPhoneChoice] = useState<PhoneChoiceValue>('on_file');
  const [phone, setPhone] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [serverPhoneError, setServerPhoneError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The sidebar and the phone bar both confirm; a double submit sends one request.
  const inFlight = useRef(false);

  const latestAt = link?.latestAt ?? nowIso;
  const slotsByDay = useMemo(
    () => buildSlots({ nowIso, latestAtIso: latestAt, timeZone, showLaterDates }),
    [nowIso, latestAt, timeZone, showLaterDates]
  );
  const limits = bookingLimits(nowIso, latestAt, showLaterDates);
  const dayKeys = [...slotsByDay.keys()];
  const [selectedDay, setSelectedDay] = useState<string | undefined>(dayKeys[0]);
  // Changing zone or range can drop the selected day; fall back to the first one.
  const activeDay = selectedDay && slotsByDay.has(selectedDay) ? selectedDay : dayKeys[0];
  const year = dayjs(nowIso).tz(timeZone).year();

  if (!link || pageState === 'invalid') {
    return (
      <BookingShell companyName={companyName} year={year}>
        <Stack sx={[styles.narrowColumn, styles.invalidColumn]}>
          <BookingDeadEnd companyName={companyName} state="invalid" />
        </Stack>
      </BookingShell>
    );
  }

  if ((pageState === 'confirmed' || pageState === 'already_booked') && confirmedBooking) {
    return (
      <BookingShell companyName={companyName} year={year}>
        <BookingSummary
          booking={confirmedBooking}
          companyName={companyName}
          link={link}
          variant={pageState}
        />
      </BookingShell>
    );
  }

  // Expired, cancelled, closed, or no slot left to offer.
  if (pageState !== 'picking' || !activeDay) {
    const deadEnd =
      pageState === 'expired' || pageState === 'cancelled' || pageState === 'closed'
        ? pageState
        : 'expired';
    return (
      <BookingShell companyName={companyName} year={year}>
        <Stack sx={styles.narrowColumn}>
          <PageTitle
            centred
            subtitle={`${link.jobTitle} · ${companyName}`}
            title="Book your phone interview"
          />
          <BookingDeadEnd companyName={companyName} state={deadEnd} />
        </Stack>
      </BookingShell>
    );
  }

  const differentNumber = phoneChoice === 'different';
  const numberError = differentNumber ? phoneError(phone) : null;
  // Never echo an invalid number: the masked one on file stands until the
  // override is valid.
  const overrideValid = differentNumber && numberError === null;

  const confirm = async () => {
    if (!selectedSlot || numberError || !bookUrl || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setSubmitError(null);
    const result = await bookCall(bookUrl, {
      scheduledAt: selectedSlot,
      timeZone,
      phone: overrideValid ? phone : undefined
    });
    inFlight.current = false;
    setSubmitting(false);

    if (result.ok) {
      setConfirmedBooking(result.props.booking);
      setPageState(result.props.booking ? 'confirmed' : result.props.state);
      return;
    }

    const { error, message, state: serverState, booking: serverBooking } = result.error;
    if (serverBooking) {
      setConfirmedBooking(serverBooking);
      setPageState('already_booked');
    } else if (error === 'slot_unavailable') {
      setRejected((prev) => new Set(prev).add(selectedSlot));
      setErrorSlot(selectedSlot);
      setSelectedSlot(null);
    } else if (error === 'invalid_phone') {
      setServerPhoneError(message ?? 'Enter a valid phone number.');
      setPhoneTouched(true);
    } else if (serverState && serverState !== 'picking') {
      setPageState(serverState);
    } else {
      setSubmitError(TRY_AGAIN);
    }
  };

  const summary: ICallSummary = {
    slotIso: selectedSlot,
    timeZone,
    number: overrideValid ? displayPhone(phone) : link.maskedPhoneOnFile,
    numberIsOverride: overrideValid,
    blocker: (() => {
      if (!selectedSlot) return 'no_slot';
      if (numberError) return 'invalid_number';
      return null;
    })(),
    submitting,
    submitError,
    onConfirm: () => {
      confirm().catch(() => setSubmitError(TRY_AGAIN));
    }
  };

  return (
    <BookingShell companyName={companyName} year={year}>
      <PageTitle subtitle={`${link.jobTitle} · ${companyName}`} title="Book your phone interview" />

      <Box sx={styles.layout}>
        <Stack sx={styles.mainColumn}>
          <Stack component="section" sx={styles.card}>
            <Stack sx={styles.introRow}>
              <Box aria-hidden sx={styles.introIcon}>
                <RecordVoiceOverIcon />
              </Box>
              <Stack sx={{ rowGap: 0.5 }}>
                <Typography sx={styles.introLead}>
                  {`Hi ${link.candidateFirstName}, choose a time for Rosie to call you.`}
                </Typography>
                <Typography sx={styles.body}>
                  {`Rosie is ${companyName}’s AI interviewer. The call takes about 10 minutes and covers ${link.questionCount} question${link.questionCount === 1 ? '' : 's'} about the role. Pick any weekday time between 9:30am and 7pm.`}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Stack
            aria-labelledby="booking-time-heading"
            component="section"
            sx={[styles.card, styles.pickerCard]}
          >
            <Stack sx={{ rowGap: 1 }}>
              <Typography component="h2" id="booking-time-heading" sx={styles.cardHeading}>
                Choose a time
              </Typography>
              <TimeZoneControl
                detectedTimeZone={detectedTimeZone}
                referenceIso={nowIso}
                timeZone={timeZone}
                onChange={(zone) => {
                  setTimeZone(zone);
                  setSelectedSlot(null);
                  setErrorSlot(null);
                }}
              />
            </Stack>
            <SlotPicker
              canShowLater={limits.canShowLater}
              errorSlot={errorSlot}
              onShowLater={() => setShowLaterDates(true)}
              selectedDay={activeDay}
              selectedSlot={selectedSlot}
              showLaterDates={showLaterDates}
              slotsByDay={slotsByDay}
              timeZone={timeZone}
              unavailable={rejected}
              onSelectDay={(day) => {
                setSelectedDay(day);
                setSelectedSlot(null);
              }}
              onSelectSlot={(iso) => {
                setSelectedSlot(iso);
                setErrorSlot(null);
              }}
            />
          </Stack>

          <PhoneChoice
            choice={phoneChoice}
            error={phoneTouched ? (serverPhoneError ?? numberError) : null}
            maskedPhoneOnFile={link.maskedPhoneOnFile}
            onChoiceChange={setPhoneChoice}
            onPhoneBlur={() => setPhoneTouched(true)}
            phone={phone}
            onPhoneChange={(value) => {
              setPhone(value);
              setServerPhoneError(null);
            }}
          />
        </Stack>

        <Box component="aside" sx={styles.sidebar}>
          <CallSummaryPanel {...summary} />
        </Box>
      </Box>

      <MobileConfirmBar {...summary} />
    </BookingShell>
  );
};

export default withScoutTheme(BookingPage);
